/**
 * UI element: modals
 * requires: bootstrap 5
 */

/**
 * Tools to deal with focus management esp. in components.
 */

/**
* Adapted from https://github.com/salesforce/kagekiri
* Get children of a node, including getting slotted content.
* @param {HTMLElement} node Node to check
* @returns {HTMLCollection|HTMLElement[]}
*/
const getChildren = function (node) {
    // is it a slot.
    if (typeof node.assignedElements === 'function') {
        const assigned = node.assignedElements();
        // slotted content or the default
        return assigned.length ? assigned : node.children;
    } else {
        return node.children;
    }
};

/**
 * Is an element hidden.
 * @param {HTMLElement} el Element to check
 * @returns {Boolean}
 */
const isElementHidden = function (el) {
    return !(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
};

/**
 * Is an element focusable.
 * This probably doesn't cover ALL possibilities, but its a decent start.
 * @param {HTMLElement} element Element to check.
 * @param {Boolean} keyboardOnly Are we only looking for tabbable focus.
 * @returns {Boolean}
 */
const isFocusable = function (element, keyboardOnly = false) {
    const tagName = element.tagName;
    // Just in case.
    if (tagName === undefined) {
        return false;
    }
    if (isElementHidden(element)) {
        return false;
    }
    // Will be false-y if not set/available.
    const disabled = element.disabled;
    // Convert to integer or NaN if tabindex is not set.
    const tabIndex = parseInt(element.getAttribute('tabindex'), 10);
    const contentEditable = element.getAttribute('contenteditable');

    switch (tagName) {
        case 'INPUT':
        case 'TEXTAREA':
        case 'SELECT':
        case 'BUTTON':
        case 'A':
            if (tabIndex === -1) {
                return false;
            }
            if (disabled) {
                return false;
            }
            // for input[type=hidden]
            if (element.type === 'hidden') {
                return false;
            }
            if (tagName === 'A' && !element.href) {
                return false;
            }
            return true;
        default:
            if (keyboardOnly && tabIndex >= 0) {
                return true;
            } else if (!keyboardOnly && tabIndex >= -1) {
                return true;
            }
            if (contentEditable === 'true') {
                return true;
            }
    }
    return false;
};

/**
 * Get focusable elements recursively.
 * @param {HTMLElement} element Element to get children of
 * @param {Boolean} keyboardOnly Only tabbable elements?
 * @param {Boolean} single Only get the first one.
 * @returns {HTMElement[]}
 */
const getFocusableChildren = function (element, keyboardOnly = false, single = false) {
    let focusables = [];
    const children = Array.from(getChildren(element));
    if (children.length === 0) {
        return focusables;
    }
    for (const child of children) {
        // check if element is focusable.
        if (isFocusable(child, keyboardOnly)) {
            focusables.push(child);
            if (single) {
                return focusables;
            }
        }
        const childFocusables = getFocusableChildren(child, keyboardOnly, single);
        if (single && childFocusables.length === 1) {
            return childFocusables;
        }
        focusables = focusables.concat(childFocusables);
    }
    return focusables;
};

/**
 * Get focusable elements in the component
 * Shadow DOM and slotted elements
 * Theoretically in the correct order.
 * @param {HTMLElement|ShadowRoot} parent Parent to look in.
 * @param {Boolean} keyboardOnly Only tabbable elements?
 * @param {Boolean} single Only get the first one.
 * @returns {HTMLElement[]}
 */
const findFocusables = function (parent, keyboardOnly = false, single = false) {
    return getFocusableChildren(parent, keyboardOnly, single);
};

/**
 * Get the first focusable element in parent.
 * @param {HTMLElement|ShadowRoot} parent Parent to look in.
 * @returns {HTMLElement|null}
 */
const findFirstFocusable = function (parent) {
    return findFocusables(parent, false, true).shift();
};
/**
 * Get the first tabbing focusable element in parent.
 * @param {HTMLElement|ShadowRoot} parent Parent to look in.
 * @returns {HTMLElement|null}
 */
const findFirstTabFocusable = function (parent) {
    return findFocusables(parent, true, true).shift();
};
/**
 * Return last focusable element in parent.
 * @param {HTMLElement|ShadowRoot} parent Parent to look in.
 * @returns {HTMLElement|null}
 */
const findLastTabFocusable = function (parent) {
    const focusables = findFocusables(parent, true);
    return focusables[focusables.length - 1];
};

/**
 * Find first focusable element in parent and focus on it.
 * @param {HTMLElement|ShadowRoot} parent Parent to look in.
 */
const focusFirst = function (parent) {
    const focusable = findFirstFocusable(parent);
    if (focusable) {
        focusable.focus();
    }
};

/**
 * Get focused element.
 * @returns {HTMLElement|null}
 */
const deepActiveElement = function () {
    let a = document.activeElement;
    while (a && a.shadowRoot && a.shadowRoot.activeElement) {
        a = a.shadowRoot.activeElement;
    }
    return a;
};

class ModalBS5 {
    modal_buttons = [];
    btn_callbacks = {};
    modal = null;
    #boundKeyboardEvents = function () { };

    /**
     * @param {Object} config all of the config properties are optional, but you at least need to set config.content or config.url
     * @param {String} [config.id=s-sui-modal] optional modal ID (you only need this for opening multiple modals at the same time)
     * @param {String} [config.title] text for header of modal (if absent header will not be displayed)
     * @param {String|HTMLElement} [config.content] html content for modal (inserted into .modal-body) Accepts html string or whole element. (alternately use config.url to load content)
     * @param {String} [config.url] content will be loaded into .modal-content from this url if present (format returned data as json object with properties: title, content, buttons (same as config.title, config.content, and config.buttons)
     * @param {Function} [config.callback] a function called when the modal is shown, the modal object is passed as the first parameter.
     * @param {Function} [config.closeCallback] a function called when the modal is closed, the modal object is passed as the first parameter.
     * @param {Boolean} [config.footer=false] display footer (buttons) or not, overridden if you set config.buttons
     * @param {String} [config.size] accepts 'large' to apply the modal-lg class (wider modal than normal), otherwise use BS classes
     * @param {Object[]} [config.buttons] button settings for use in footer
     * @param {Object} [config.buttons[]]
     * @param {String} [config.buttons[].bclass] class(es) to add to the button (like btn-primary) defaults to btn-default
     * @param {String} [config.buttons[].label] text of the button
     * @param {Function} [config.buttons[].callback] callback applied when button is clicked, the modal object will be found in e.data.modal of the click event
     * @param {Boolean} [config.returnfocus=true] return focus to the element that was active when the modal opened (usually that should be the button that triggered the modal)
     * @param {Object} [config.transText] Translatated text for default button and message text.
     * @param {Boolean} [config.focus] Set to false to disable BS focus handling until we fix an issue with CKE in the modals
     * @param {Boolean} [config.reusable] Don't destroy/empty on close.
     */
    constructor({
        id = '',
        title = '',
        content = '',
        url = null,
        callback = null,
        closeCallback = null,
        footer = false,
        size = '',
        buttons = [],
        returnfocus = true,
        transText = null,
        reusable = false,
    }) {
        this.id = id ? id : `s-sui-modal-${Math.floor(Math.random() * 10000)}`;
        this.title = title;
        this.content = content;
        this.url = url;
        this.callback = callback;
        this.closeCallback = closeCallback;
        this.footer = footer;
        this.size = size;
        if (Array.isArray(buttons)) {
            this.modal_buttons = buttons;
        }
        this.returnfocus = returnfocus;
        this.transText = {
            close: transText?.close ?? 'Close',
            error: transText?.error ?? 'Error',
            unknown: transText?.unknown ?? 'Unknown',
            invalidResponse: transText?.invalidResponse ?? 'Invalid response received',
            toggle: transText?.toggle ?? 'Toggle Dropdown',
        };
        this.opener = deepActiveElement();
        this.reusable = reusable;
        this.el = document.getElementById(this.id);
        if (!this.el) {
            this.el = this.#generateHtml();
            this.#insertModal();
        }

        if (this.url !== null) {
            this.#retrieve();
        } else {
            this.#draw();
        }
    }

    #generateHtml() {
        const template = document.createElement('template');
        template.innerHTML = `<div class="modal" tabindex="-1" role="dialog" aria-labelledby="s-sui-modal-header" id="${this.id}">
            <div class="modal-dialog ${this.size === 'large' ? 'modal-lg' : this.size}">
                <div class="modal-content"></div>
            </div>
        </div>`;
        return template.content.firstElementChild;
    }

    #insertModal() {
        if (this.id === 's-sui-modal') {
            document.body.prepend(this.el);
        } else {
            // put non-default modals AFTER any other modals
            const modals = document.querySelectorAll('div.modal');
            if (modals.length > 0) {
                const last = modals[modals.length - 1];
                last.insertAdjacentElement('afterend', this.el);
            } else {
                document.body.prepend(this.el);
            }
        }
    }

    /**
     * Callback: Modal content retrieval.
     * @param {Object} data Response payload.
     */
    #retrieveSuccess({
        title = null,
        content = null,
        buttons = null,
        footer = false,
    }) {
        if (title) {
            this.title = title;
        }
        if (content) {
            this.content = content;
        }
        if (buttons) {
            this.modal_buttons = buttons;
        }
        this.footer = !!footer;

        this.#draw();
    }

    /**
     * Callback: Modal content retrieval. Failed.
     * @param {Object} jqXhr JQuery response object.
     */
    #retrieveFail(jqXhr) {
        let error = this.transText.unknown;
        if (jqXhr.responseText) {
            try {
                const response = JSON.parse(jqXhr.responseText);
                if (response.error) {
                    error = response.error;
                }
            } catch {
                error = this.transText.invalidResponse;
            }
        }
        this.content = `${this.transText.error}: ${error}`;
        this.footer = true;
        this.#draw();
    }

    /**
     * retrieve content
     */
    #retrieve() {
        jQuery.ajax({
            url: this.url,
            cache: false,
            method: 'GET',
            dataType: 'json',
        })
            .done(this.#retrieveSuccess.bind(this))
            .fail(this.#retrieveFail.bind(this));
    }

    /**
     * Handler: Bootstrap modal show event.
     */
    #handleShowEvent() {
        if (this.callback !== null) {
            this.callback(this);
        }
    }

    /**
     * Handler: Bootstrap modal shown event (After modal is visible)
     */
    #handleShownEvent() {
        focusFirst(this.el);
    }

    /**
     * Handler: Bootstrap modal hide event.
     */
    #handleHideEvent() {
        // return focus to opener (button/link)
        if (this.returnfocus && this.opener instanceof HTMLElement) {
            this.opener.focus();
        }

        // call close callback with modal as first argument.
        if (this.closeCallback !== null) {
            this.closeCallback(this);
        }
        // don't clean up everything if this is reusable
        if (this.reusable) {
            return;
        }
        // clean up events and html (for DOM element reuse)
        this.el?.remove();
        this.el = null;

        // make sure callbacks are cleared, else they can end up firing multiple times.
        this.callback = null;
        this.closeCallback = null;

        if (this.#boundKeyboardEvents) {
            document.removeEventListener('keydown', this.#boundKeyboardEvents, true);
        }
    };

    /**
     * Close the modal, which triggers the hidden.bs.modal event handler above
     */
    close() {
        this.modal?.hide();
    }

    open() {
        this.modal?.show();
    }

    #generateFooterButtons() {
        let template = '';
        this.modal_buttons.forEach(({ label = '', bclass = 'btn-light', callback = null }, k) => {
            if (!label) {
                return;
            }
            template += `<button type="button" class="btn ${bclass} btn-modal-${k}" >${label}</button>`;
            if (callback) {
                this.btn_callbacks[`btn-modal-${k}`] = callback;
            }
        });
        return template;
    }

    #generateFooter() {
        this.btn_callbacks = {}; // reset here just in case

        return `<div class="modal-footer">
            ${this.modal_buttons.length === 0
                    ? `<button type="button" class="btn btn-light" data-bs-dismiss="modal">${this.transText.close}</button>`
                    : this.#generateFooterButtons()
            }
        </div>`;
    }

    /**
     * Handler: Keyboard events to:
     * - Close on Esc
     * - Trap tab focus into modal.
     * @param {KeyboardEvent} ev Keydown event
     */
    keyboardEvents(ev) {
        if (ev.key === 'Escape') {
            // if CKE dialog is open things break so... don't close modal in that case
            if (document.querySelector('.cke_dialog_background_cover')?.checkVisibility() ?? false) {
                // this intercepts the event already set on the native BS modal.
                ev.stopPropagation();
                return;
            }
            // We don't need to do anything else here BS native will handle the close
            return;
        }
        if (ev.key !== 'Tab') {
            return;
        }
        // Get current active/focused element.
        const currentEl = deepActiveElement();
        if (ev.shiftKey) {
            // Shift tab, only concerned with first element.
            if (currentEl === findFirstFocusable(this.el)
                || currentEl === findFirstTabFocusable(this.el)) {
                // Go to the last element in the modal.
                const focusable = findLastTabFocusable(this.el);
                if (focusable) {
                    ev.preventDefault();
                    focusable.focus();
                }
            }
            return;
        }
        // Tab (no shift), only concerned with last element.
        if (currentEl === findLastTabFocusable(this.el)) {
            // Go to the first element in the modal.
            const focusable = findFirstTabFocusable(this.el);
            if (focusable) {
                ev.preventDefault();
                focusable.focus();
            }
        }
    }

    #addListeners() {
        /**
         * Apply any callbacks to the show event (this happens before the viewer sees the modal
         */
        this.el.addEventListener('show.bs.modal', this.#handleShowEvent.bind(this));

        /**
         * When viewer sees the modal
         */
        this.el.addEventListener('shown.bs.modal', this.#handleShownEvent.bind(this));

        /**
         * clean up modal on close, making sure to empty the html and unbind events
         */
        this.el.addEventListener('hidden.bs.modal', this.#handleHideEvent.bind(this));

        this.#boundKeyboardEvents = this.keyboardEvents.bind(this);
        document.addEventListener('keydown', this.#boundKeyboardEvents, true);
    }

    /**
     * Format the modal content and apply appropriate callbacks and handlers and such
     * this will also show the modal
     */
    #draw() {
        const template = document.createElement('template');
        template.innerHTML = `<div class="modal-content">
            ${this.title !== ''
                    ? `<div class="modal-header">
                    <h5 class="modal-title" id="s-sui-modal-header">${this.title}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${this.transText.close}"></button>
                </div>`
                    : ''
            }
            <div class="modal-body"></div>
            ${this.footer || this.modal_buttons.length > 0 ? this.#generateFooter() : ''}
        </div>`;

        const html = template.content.firstElementChild;
        this.el.querySelector('.modal-content')?.replaceWith(html);

        // if this.content is a HTMLElement we need to append it
        // this let's us keep any events on the content.
        const body = this.el.querySelector('.modal-body');
        if (typeof this.content !== 'string') {
            if (body) {
                body.innerHTML = '';
                body.append(this.content);
            };
        } else {
            // @todo inserting scripts only works with jquery
            // because it uses eval on them or something
            // find vanilla solution later or else don't sent scripts with content (ideal)
            // innerHTML doesn't work. Neither does putting this.content into the template.
            // seems like it only works if you specifically insert a script element...?
            // Range.createContextualFragment works in Chrome but won't be default in FF
            jQuery(body).html(this.content);
        }

        /**
         * Apply button callbacks
         */
        Object.keys(this.btn_callbacks).forEach((key) => {
            if (typeof this.btn_callbacks[key] === 'function') {
                this.el.querySelector(`.${key}`)?.addEventListener('click', (ev) => {
                    ev.data = ev.data || {};
                    ev.data.modal = this;
                    this.btn_callbacks[key](ev);
                });
            }
        });

        // in case this element is already a BS modal destroy it
        // eslint-disable-next-line no-undef
        const existing = bootstrap.Modal.getInstance(this.el);
        if (existing) {
            existing.dispose();
        }
        // eslint-disable-next-line no-undef
        this.modal = new bootstrap.Modal(this.el, {
            focus: false,
        });
        this.#addListeners();

        // Hide toolip on opener if present
        // for some reason these can get stuck otherwise
        // eslint-disable-next-line no-undef
        const tip = this.opener ? bootstrap.Tooltip.getInstance(this.opener) : null;
        if (tip) {
            tip.hide();
        }

        this.modal.show();
    }
}

window.springSpace = window.springSpace || {};
window.springSpace.sui = window.springSpace.sui || {};
window.springSpace.sui.modal = ModalBS5;

// BS5: Override the one in la2.js while we transition
window.closeModal = function (modal_id = '') {
    if (!modal_id) {
        document.querySelectorAll('.modal')?.forEach((el) => {
            el.querySelector('button[data-bs-dismiss="modal"]')?.click();
        });
        return;
    }
    document.querySelector(`#${modal_id} button[data-bs-dismiss="modal"]`)?.click();
};

/*
    Example usage:

    Basic modal with title and content already available
    var modal = new ModalBS5({ title: 'A Modal', content: '<p>Hello, modal.</p>' });

    Basic modal with callback (this in the callback will refer to the modal object)
    var modal = new ModalBS5({ title: 'A Modal', content: '<p>Hello, modal.</p>', callback: function(){ this.$el.find('p').css('color', 'pink'); } });

    Basic ajax loaded modal
    var modal = new ModalBS5({ url: '/process_public.php?m=qform' });

    On the server side, the ajax request should return a JSON encoded object with:
    title (optional, title of modal)
    content (html content of modal)
    footer (boolean, defaults to false, just show a basic footer with a close button)
    buttons (array of buttons for footer of modal, as an alternate to the footer boolean)
        -each button is an assoc array (or object) with: label, bclass (optional)

    Ajax with callback (first argument to the callback will refer to the modal object)
    var modal = new ModalBS5({ url: '/process_public.php?m=qform', callback: function(modal){ console.log(modal); } });

    Button callbacks are passed the modal object in e.data
    var modal = new ModalBS5({ title: 'A Modal', content: '<p>Hello, modal.</p>', footer: true, buttons: [{bclass: 'btn-primary btn-lg', label:'test close', callback: function(e){ e.data.modal.close(); } }] });

    Close the modal manually:
    modal.close();
    (You can also use the normal data-bs-dismiss="modal" attribute on buttons, that will still trigger an event handler to clean-up the modal)

    Trigger function on close:
    config.closeCallback = function(modal) {
        // do something here.
    };
    // most likely you'll want to bind the function to another object for anything complicated.

    Custom "confirm" modal
    var confirm = new ModalBS5({
        content: 'Are you sure you want to proceed?',
        buttons: [
            { label: 'No', callback: function(e){ e.data.modal.close(); } },
            { label: 'Yes', bclass: 'btn-primary', callback: function(e){ doSomething(); e.data.modal.close(); } }
        ]
    });
*/
